import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import * as Square from '@square/web-sdk';
import _ from 'lodash';
import { Button } from '@/components/Elements';
import { useAddCreditCard } from '../api/addCreditCard';
import { Loader } from '@/components/Loader/Loader';

type SquareFormProps = {
  locationId: string;
  onSuccess: (token: string, verificationToken?: string,card?: any) => void;
  onError: (err?: any) => void;
  label?: string;
  saveCard?: boolean;
  amount?: number;
  loading?: boolean;
  fullBtn?: boolean;
  authOnly?: boolean;
  businessLocationId?: any;
};

export type SquareFormHandle = {
  verify3DS: (storedToken?: string) => void;
};

export const SquareForm = forwardRef<SquareFormHandle, SquareFormProps>(
  ({ onSuccess, onError, label, saveCard, locationId, amount, loading, fullBtn, authOnly, businessLocationId }: SquareFormProps, ref) => {
    const [card, setCard] = useState<Square.Card>();
    const [payment, setPayment] = useState<Square.Payments>();
    const [isLoading, setIsLoading] = useState(true);
    const addCreditCardMutation = useAddCreditCard();
    const cardElRef = useRef<HTMLDivElement | null>(null);

    const verifyBuyer = async (token: string, payments?: Square.Payments) => {
      try {
        if (!payments) {
          throw new Error('Unable to initialize payment');
        }

        const verifyProps = saveCard ? { intent: 'STORE' } : { intent: 'CHARGE', amount: amount?.toFixed(2), currencyCode: 'USD' };

        const verificationDetails: any = {
          ...verifyProps,
          billingContact: {},
        };
    
        const verificationResults = await payments.verifyBuyer(
          token,
          verificationDetails
        );

        return verificationResults?.token;
      } catch (ex) {
        console.error('=======================================> Error verifying user: ', JSON.stringify(ex));
        onError(ex);
      }
    }; 

    const tokenize = useCallback(async (verify?: boolean) => {
      try {
        if (!card || !payment) {
          throw new Error('Unable to initialize payment');
        }

        setIsLoading(true);

        const result = await card.tokenize();
        if (!result.token) throw new Error('Please enter valid card details');
        let token;

        if (verify) {
          token = await verifyBuyer(result.token, payment);

          if (!token) throw new Error('Unable to complete verification');
        }

        if (saveCard) {
          await addCreditCardMutation.mutateAsync({
            data: {
              paymentMethodId: result.token,
              verificationToken: token,
              paymentProvider: 'square',
              businessLocationId : businessLocationId,
              ...result.details,
              
            },
          });
        }

        setIsLoading(false);
        const cardDetails = {
          number : result?.details?.card?.last4,
          squareCardId : result?.token,
          verificationToken: token,
          paymentProvider: 'square',
          ...result?.details?.card,
        }
        onSuccess(result.token, token,cardDetails);
      } catch (ex) {
        console.error('=======================================> Error tokenizing card: ', ex);
        const code = _.get(ex, 'response.data.errors.0.code');

        if (code === 'CARD_DECLINED_VERIFICATION_REQUIRED') {
          tokenize(true);
        } else {
          onError(ex);
          setIsLoading(false);
        }
      }
    }, [card, payment]);

    useEffect(() => {
      const initSquare = async () => {
        try {
          const payments = await Square.payments(process.env.REACT_APP_SQUARE_APP_ID as string, locationId);

          if (payments) {
            setPayment(payments);
          }
        } catch(e) {
          console.error('=======================================> Error initializing square: ', e);
          onError(e);
        }

        authOnly && setIsLoading(false);
      }

      initSquare();
    }, []);

    useEffect(() => {
      const initCard = async () => {
        if (authOnly || !payment) {
          return;
        }

        try {
          const card = await payment.card();
          setCard(card);

          if (cardElRef && cardElRef.current) {
            await card.attach(cardElRef.current);
          }
        } catch (e) {
          console.error('=======================================> Error initializing card: ', e);
          onError(e);
        }

        setIsLoading(false);
      }

      initCard();
    }, [authOnly, payment]);

    useImperativeHandle(ref, () => ({
      async verify3DS (storedToken?: string) {
        try {
          if (storedToken) {
            const token = await verifyBuyer(storedToken, payment);

            if (!token) {
              return onError(new Error('Unable to complete verification'));
            }

            onSuccess(storedToken, token);
          } else {
            await tokenize(true);
          }
        } catch (e) {
          onError(e);
        }
      }
    }));

    if (authOnly) return null;

    return (
      <>
        <div ref={cardElRef}></div>
        <div className="credit-card-form-action">
       {!isLoading && <button
          className="petsked-button button-dark"
          onClick={() => tokenize()}
          disabled={loading || isLoading}
        >
          {isLoading ? 'Adding Card...' : 'Save Card'}
        </button>}
        {isLoading ? <Loader text='Loading...' /> :''}
      </div>
        {/* <Button className={fullBtn ? 'w-full' : ''}
          onClick={() => tokenize()}
          isLoading={loading || isLoading}
          disabled={loading || isLoading}
        >{label || 'Save Card'}</Button> */}
      </>
    );
  }
);

SquareForm.displayName = 'SquareForm'; // fix eslint warning
